<template>
  <Teleport to="body">
    <div class="fixed inset-0 z-[9999] flex items-end justify-center">
      <div class="absolute inset-0 bg-black/40 backdrop-blur-sm" @click="cancel" />

      <!-- Отступ снизу — под системные кнопки Android (--sheet-bottom в
           style.css). Раньше здесь стояла --safe-bottom, которой нигде нет,
           и в Android-приложении кнопки уходили под панель навигации. -->
      <div class="relative w-full max-w-[430px] bg-cream rounded-t-2xl px-5 pt-5 animate-slide-up"
           style="font-family: Inter, sans-serif; padding-bottom: calc(24px + var(--sheet-bottom, 0px));">
        <div class="flex items-center justify-between mb-1">
          <span class="text-18 font-semibold text-ink">Голосовой ввод</span>
          <button @click="cancel" class="w-8 h-8 flex items-center justify-center rounded-full text-ink active:bg-line transition-colors">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>

        <!-- Нет доступа к микрофону или запись не началась -->
        <template v-if="error">
          <p class="text-14 text-ink/70 mt-3 mb-6">{{ error }}</p>
          <div class="flex gap-2">
            <button @click="cancel" class="flex-1 py-3 rounded-2xl bg-white text-ink text-15 font-semibold border border-line">Закрыть</button>
            <button @click="start" class="flex-1 py-3 rounded-2xl bg-ink text-cream text-15 font-semibold">Попробовать снова</button>
          </div>
        </template>

        <template v-else>
          <p class="text-14 text-ink/70 mb-4">
            <template v-if="context === 'chat'">{{ ty ? 'Надиктуй' : 'Надиктуйте' }} сообщение Зое. Текст появится в поле ввода — его можно проверить и отправить.</template>
            <template v-else>{{ ty ? 'Назови' : 'Назовите' }} блюда и сколько съедено — например:
              «овсянка на молоке двести грамм, банан и кофе с молоком».</template>
          </p>

          <!-- Волна: уровень звука за последние секунды, новое справа -->
          <div class="bg-white rounded-2xl px-4 flex items-center justify-center gap-[3px] overflow-hidden" style="height: 84px;">
            <span v-for="(v, i) in bars" :key="i" class="bar" :style="{ height: Math.max(4, Math.round(v * 60)) + 'px', opacity: recording ? 1 : 0.35 }" />
          </div>

          <div class="flex items-center justify-center gap-2 mt-3 mb-5" style="font-variant-numeric: tabular-nums;">
            <span class="rec-dot" :class="{ on: recording }" />
            <span class="text-15 font-semibold text-ink">{{ recording ? timeLabel : 'Включаю микрофон…' }}</span>
            <span v-if="recording" class="text-13 text-ink/70">/ 1:00</span>
          </div>
          <p v-if="recording && quiet" class="text-13 text-ink/70 text-center -mt-3 mb-4">Не слышу голос — {{ ty ? 'говори' : 'говорите' }} ближе к микрофону</p>

          <div class="flex gap-2">
            <button @click="cancel" class="flex-1 py-3 rounded-2xl bg-white text-ink text-15 font-semibold border border-line">Отмена</button>
            <button @click="finish" :disabled="!recording || elapsedMs < MIN_MS" class="flex-[2] py-3 rounded-2xl bg-ink text-cream text-15 font-semibold disabled:opacity-40">Готово</button>
          </div>
        </template>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
// Запись голосовой заметки о еде. Открылась — сразу пишет; «Готово» отдаёт
// запись наверх ({ base64, mime, durationMs }), распознаёт её AddFoodDialog
// через POST /ai/recognize-voice. Сама запись нигде не сохраняется.
//
// Формат выбирает браузер: Chrome и Android WebView пишут webm/opus, Safari и
// iOS — mp4/aac; Gemini принимает оба (проверено 5.10.2026), перекодировать не
// нужно. Волна рисуется по AnalyserNode и на запись не влияет: если
// AudioContext не завёлся (бывает в iOS до жеста), полоски просто стоят.
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { useAuthStore } from '../store/auth.js'

defineProps({ context: { type: String, default: 'food' } })
const emit = defineEmits(['close', 'recorded'])
const authStore = useAuthStore()
const ty = computed(() => authStore.user?.address_form !== 'вы')

const MAX_MS = 60000   // дольше минуты блюдо не диктуют; сервер режет по размеру
const MIN_MS = 800     // случайное двойное нажатие
const BAR_COUNT = 40
const QUIET_AFTER_MS = 3000

const bars = ref(Array(BAR_COUNT).fill(0))
const recording = ref(false)
const elapsedMs = ref(0)
const error = ref('')
const quiet = ref(false)

const timeLabel = computed(() => {
  const s = Math.floor(elapsedMs.value / 1000)
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`
})

let stream = null
let recorder = null
let chunks = []
let audioCtx = null
let analyser = null
let rafId = 0
let startedAt = 0
let lastBarAt = 0
let peak = 0
let heardVoice = false
let cancelled = false

function pickMime() {
  const MR = window.MediaRecorder
  if (!MR?.isTypeSupported) return ''
  return ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg;codecs=opus'].find(t => MR.isTypeSupported(t)) || ''
}

function stopTracks() {
  cancelAnimationFrame(rafId)
  try { stream?.getTracks().forEach(t => t.stop()) } catch { /* уже остановлены */ }
  try { audioCtx?.close() } catch { /* уже закрыт */ }
  stream = null
  audioCtx = null
  analyser = null
}

function tick() {
  if (!recording.value) return
  const now = performance.now()
  elapsedMs.value = now - startedAt

  if (analyser) {
    const buf = new Uint8Array(analyser.fftSize)
    analyser.getByteTimeDomainData(buf)
    let sum = 0
    for (let i = 0; i < buf.length; i++) { const v = (buf[i] - 128) / 128; sum += v * v }
    const rms = Math.sqrt(sum / buf.length)
    peak = Math.max(peak, rms)
    if (rms > 0.03) heardVoice = true
    // Полоска раз в 80 мс — пик за этот отрезок, чтобы короткие слова были видны
    if (now - lastBarAt >= 80) {
      bars.value = [...bars.value.slice(1), Math.min(1, peak * 4)]
      peak = 0
      lastBarAt = now
    }
    quiet.value = !heardVoice && elapsedMs.value > QUIET_AFTER_MS
  }

  if (elapsedMs.value >= MAX_MS) { finish(); return }
  rafId = requestAnimationFrame(tick)
}

async function start() {
  error.value = ''
  cancelled = false
  chunks = []
  heardVoice = false
  quiet.value = false
  bars.value = Array(BAR_COUNT).fill(0)

  try {
    stream = await navigator.mediaDevices.getUserMedia({
      audio: { echoCancellation: true, noiseSuppression: true, channelCount: 1 },
    })
  } catch (e) {
    error.value = e?.name === 'NotAllowedError' || e?.name === 'SecurityError'
      ? 'Нет доступа к микрофону. Разрешите его для Зои в настройках телефона или браузера и попробуйте снова.'
      : 'Не удалось включить микрофон. Проверьте, что он не занят другим приложением.'
    return
  }
  if (cancelled) { stopTracks(); return }

  try {
    const mimeType = pickMime()
    recorder = new MediaRecorder(stream, { ...(mimeType ? { mimeType } : {}), audioBitsPerSecond: 32000 })
  } catch {
    stopTracks()
    error.value = 'Запись голоса на этом устройстве не поддерживается.'
    return
  }
  recorder.ondataavailable = e => { if (e.data?.size) chunks.push(e.data) }
  recorder.onstop = onRecorderStop
  recorder.start(1000)

  try {
    const Ctx = window.AudioContext || window.webkitAudioContext
    audioCtx = new Ctx()
    audioCtx.resume?.().catch(() => {})
    analyser = audioCtx.createAnalyser()
    analyser.fftSize = 1024
    audioCtx.createMediaStreamSource(stream).connect(analyser)
  } catch {
    analyser = null // волны не будет, запись идёт
  }

  startedAt = performance.now()
  lastBarAt = startedAt
  elapsedMs.value = 0
  recording.value = true
  rafId = requestAnimationFrame(tick)
}

function onRecorderStop() {
  const type = recorder?.mimeType || chunks[0]?.type || 'audio/webm'
  const durationMs = Math.round(elapsedMs.value)
  const blob = new Blob(chunks, { type })
  chunks = []
  stopTracks()
  if (cancelled) return
  if (!blob.size) { error.value = 'Запись получилась пустой. Попробуйте ещё раз.'; return }
  const reader = new FileReader()
  reader.onload = () => {
    const base64 = String(reader.result).split(',')[1] || ''
    emit('recorded', { base64, mime: type, durationMs })
  }
  reader.onerror = () => { error.value = 'Не удалось прочитать запись. Попробуйте ещё раз.' }
  reader.readAsDataURL(blob)
}

function finish() {
  if (!recording.value) return
  recording.value = false
  cancelAnimationFrame(rafId)
  try { recorder?.stop() } catch { onRecorderStop() }
}

function cancel() {
  cancelled = true
  if (recording.value) {
    recording.value = false
    try { recorder?.stop() } catch { /* не запущен */ }
  }
  stopTracks()
  emit('close')
}

onMounted(start)
onBeforeUnmount(() => {
  // Окно сняли извне (закрыли диалог) — микрофон не должен остаться включённым
  cancelled = true
  recording.value = false
  try { if (recorder?.state === 'recording') recorder.stop() } catch { /* не запущен */ }
  stopTracks()
})
</script>

<style scoped>
.bar {
  flex: none;
  width: 3px;
  border-radius: 2px;
  background: var(--ink);
  transition: height 80ms linear;
}
.rec-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: var(--ink-30, rgba(65, 41, 26, 0.3));
}
.rec-dot.on {
  background: #EA2327;
  animation: rec-pulse 1.2s ease-in-out infinite;
}
@keyframes rec-pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.35; }
}
</style>
